import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";
import {
  useFetchEmpleados,
  useUpdateEmpleado,
} from "@/shared/actions/erp/empleados";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { AlertSeverityEnum } from "@/shared/interfaces";
import { Empleado } from "@/shared/interfaces/erp/empleados";
import { useUiStore } from "@/store/ui";

export interface EmpleadosPageInterface {}

const EmpleadosPage: React.FC<EmpleadosPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_empleado);
  const returnUrlEmpleados = "/nomina/empleados";

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();
  ////alert
  const setAlert = useUiStore((s) => s.setAlert);
  const [showAlert, setShowAlert] = useState(false);
  ///* table
  const {
    searchTerm,
    globalFilter,
    pagination,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;
  ///* fetch data
  const {
    data: EmpleadosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchEmpleados({
    enabled: true,
    params: {
      identificacion: searchTerm,
      page: pageIndex + 1,
      page_size: pageSize,
      ...filterObject, /// clave id y valor
      filterByState: false, /// la solicituda a la Api aplicara los filtros, basadaos en estados
      //filtre todo los datos que se encuentra en el estado activo
    },
  });

  useEffect(() => {
    if (EmpleadosPagingRes?.data?.count === 0) {
      if (!showAlert) {
        setAlert(
          true,
          "No se encontraron los resultados buscados...!",
          AlertSeverityEnum.info,
        );
        setShowAlert(false);
      }
    }
  }, [EmpleadosPagingRes, showAlert, setAlert, setShowAlert]);
  ///* mutations
  const updateIvaMutation = useUpdateEmpleado({
    navigate,
    returnUrl: returnUrlEmpleados,
  });
  ///* handlers
  const onEdit = (original: Empleado) => {
    navigate(`${returnUrlEmpleados}/editar/${original.id_empleado}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Empleado>[]>(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "N° referencia",
        size: 200,
      },
      {
        accessorKey: "identificacion",
        header: "Identificacion",
        size: 200,
      },
      {
        accessorKey: "razon_social",
        header: "Razon social",
        size: 200,
      },
      {
        accessorKey: "direccion",
        header: "Direccion",
        size: 200,
      },
      {
        accessorKey: "email",
        header: "email",
        size: 200,
      },
      {
        accessorKey: "telefono_1",
        header: "telefono 1",
        size: 200,
      },
      {
        accessorKey: "telefono_2",
        header: "telefono 2",
        size: 200,
      },
      {
        accessorKey: "telefono_3",
        header: "telefono 3",
        size: 200,
      },
      {
        accessorKey: "tipo_Empleados",
        header: "tipo Empleados",
        size: 200,
      },
      {
        accessorKey: "pais",
        header: "pais",
        size: 200,
      },
      {
        accessorKey: "provincia",
        header: "provincia",
        size: 200,
      },
      {
        accessorKey: "ciudad",
        header: "ciudad",
        size: 200,
      },
      {
        accessorKey: "zona",
        header: "zona",
        size: 200,
      },
      {
        accessorKey: "sector",
        header: "sector",
        size: 200,
      },
      {
        accessorKey: "empresa",
        header: "empresa",
        size: 200,
      },
      {
        accessorKey: "area_data.nombre",
        header: "area",
        size: 200,
      },

      {
        accessorKey: "departamento",
        header: "departamento",
        size: 200,
      },
      {
        accessorKey: "cargo",
        header: "cargo",
        size: 200,
      },

      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? "Activo" : "Inactivo"}
            setEstadoSwitch={(Empleados1) => {
              updateIvaMutation.mutate({
                id: row.original.id_empleado!,
                data: {
                  ...row.original,
                  estado: Empleados1 as boolean,
                },
              });
            }}
          />
        ),
      },
    ],
    [],
  );
  return (
    <SingleTableBoxScene
      title="Empleados"
      urlRedirect={`${returnUrlEmpleados}/crear`}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por Identificación"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<Empleado>
        columns={columns}
        data={EmpleadosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={EmpleadosPagingRes?.data?.count}
        // // actions
        actionsColumnSize={120}
        // crud
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};
export default EmpleadosPage;
